import React, { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle,
  XCircle,
  Clock,
  PlayCircle,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const ExecutionHistory = ({ workflowId }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchExecutions = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      
      const response = await axios.get(
        `${API_URL}/api/workflows/${workflowId}/executions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setExecutions(response.data.executions || []);
    } catch (error) {
      console.error("Failed to fetch execution history:", error);
      toast.error("Failed to load execution history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workflowId) {
      fetchExecutions();
      // Auto-refresh every 30 seconds
      const interval = setInterval(fetchExecutions, 30000);
      return () => clearInterval(interval);
    }
  }, [workflowId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "running":
        return <PlayCircle className="w-4 h-4 text-blue-500 animate-pulse" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: "default",
      failed: "destructive",
      running: "secondary",
    };
    return (
      <Badge variant={variants[status] || "outline"} className="capitalize">
        {status}
      </Badge>
    );
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "N/A";
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const handleViewDetails = (execution) => {
    setSelectedExecution(execution);
    setShowDetails(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No executions yet</p>
        <p className="text-xs mt-2">
          Activate your workflow or run a test to see execution history
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Execution History</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchExecutions}
            className="h-7 px-2"
          >
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="space-y-2">
            {executions.map((execution) => (
              <div
                key={execution.execution_id}
                className="border rounded-lg p-3 hover:bg-accent/50 cursor-pointer transition-colors"
                onClick={() => handleViewDetails(execution)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(execution.status)}
                    <span className="text-sm font-medium">
                      {execution.trigger_type || "Manual"}
                    </span>
                  </div>
                  {getStatusBadge(execution.status)}
                </div>

                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between">
                    <span>Started:</span>
                    <span>
                      {formatTimeAgo(execution.started_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Duration:</span>
                    <span>
                      {formatDuration(execution.started_at, execution.completed_at)}
                    </span>
                  </div>
                </div>

                {execution.error && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-red-500">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{execution.error}</span>
                  </div>
                )}

                <div className="mt-2 flex items-center justify-end">
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Execution Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedExecution && getStatusIcon(selectedExecution.status)}
              Execution Details
            </DialogTitle>
            <DialogDescription>
              {selectedExecution &&
                new Date(selectedExecution.started_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {selectedExecution && (
            <div className="space-y-4">
              {/* Status Overview */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(selectedExecution.status)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-sm font-medium">
                    {formatDuration(
                      selectedExecution.started_at,
                      selectedExecution.completed_at
                    )}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Trigger Type</p>
                  <p className="text-sm font-medium">
                    {selectedExecution.trigger_type || "Manual"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Execution ID</p>
                  <p className="text-xs font-mono">
                    {selectedExecution.execution_id}
                  </p>
                </div>
              </div>

              {/* Error Message */}
              {selectedExecution.error && (
                <div className="border border-red-200 bg-red-50 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900 mb-1">
                        Error
                      </p>
                      <p className="text-xs text-red-700">
                        {selectedExecution.error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Logs */}
              {selectedExecution.logs && selectedExecution.logs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold">Execution Logs</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedExecution.logs.map((log, index) => (
                      <div
                        key={index}
                        className="border rounded-lg p-2 text-xs"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{log.step || `Step ${index + 1}`}</span>
                          <span className="text-muted-foreground">
                            {log.timestamp &&
                              new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-muted-foreground">{log.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExecutionHistory;
